import { addItemButtonSelectors, getSelectorAttribute } from '@weft/app-selectors';
import { filterCommercialTermsProduct, filterCommissionProducts, isFixedProduct, isUsageProduct } from '@weft/services';
import type { FixedProductInput, PromotionInput } from '@weft/types';
import { isNotNullOrUndefined, raise, withDefault } from '@weft/utils';
import type { FieldArrayPath, FieldArrayPathValue } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import type { PlanInputFormUsageProduct } from '../../../../../../../libs/types/src/entities/product/plan-input-form-usage-product';
import { Menu, getMenuItemsWithHeaders } from '../../../../components/design/menu';
import { PlusIcon } from '../../../../components/icons/PlusIcon';
import { getDefaultCommercialTermsProduct, getDefaultCommissionProduct, getDefaultCreditPromotion, getDefaultFixedPerUnitProduct,
  getDefaultFixedProduct, getDefaultInstallmentsProduct, getDefaultPerUnitUsageProduct, getDefaultSubscriptionProduct,
  getDefaultTiersUsageProduct, getDefaultVolumeUsageProduct } from '../create-plan-page-consts/add-item-button-consts';
import type { NewPlanForm } from './CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from './CreatePlanForm/create-plan-form-types/plan-input-form';

type AddItemButtonProps = {
  onClick?: () => void;
};

export const AddItemButton: React.FC<AddItemButtonProps> = ({ onClick }) => {
  const { getValues, setValue } = useFormContext<PlanInputForm, NewPlanForm>();

  const fixedProducts = withDefault(getValues('fixedProducts'), []);
  const usageProducts = withDefault(getValues('usageProducts'), []);
  const products = [...fixedProducts, ...usageProducts];
  const [commissionProduct] = filterCommissionProducts(products as FixedProductInput[]);
  const commercialTermsProduct = filterCommercialTermsProduct(products as FixedProductInput[]);

  const handleProductClick = (defaultProductCallback: () => FixedProductInput | PlanInputFormUsageProduct) => {
    return () => {
      const defaultProduct = defaultProductCallback();
      const productsPath = getCorrectFieldArrayPath(defaultProduct);
      const currentProducts = withDefault(getValues(productsPath), []);
      const updatedProducts = [...currentProducts, defaultProduct];
      setValue(productsPath, updatedProducts as FieldArrayPathValue<PlanInputForm, typeof productsPath>);
    };
  };

  const handlePromotionClick = (defaultPromotionCallback: () => PromotionInput) => {
    return () => {
      const currentPromotions = getValues('promotions') ?? [];
      const defaultPromotion = defaultPromotionCallback();
      const updatedPromotions = [...currentPromotions, defaultPromotion];
      setValue('promotions', updatedPromotions);
    };
  };

  return (
    <Menu
      align="right"
      menuItemsWithHeaders={getMenuItemsWithHeaders({
        onFixedPlanClick: handleProductClick(getDefaultFixedProduct),
        onUsagePlanClick: handleProductClick(getDefaultTiersUsageProduct),
        onPerUnitPlanClick: handleProductClick(getDefaultPerUnitUsageProduct),
        onPromotionsPlanClick: handlePromotionClick(getDefaultCreditPromotion),
        onSubscriptionClick: handleProductClick(getDefaultSubscriptionProduct),
        onCommissionClick: handleProductClick(getDefaultCommissionProduct),
        onVolumeClick: handleProductClick(getDefaultVolumeUsageProduct),
        onFixedPerUnitClick: handleProductClick(getDefaultFixedPerUnitProduct),
        onInstallmentsClick: handleProductClick(getDefaultInstallmentsProduct),
        onCommercialTermsClick: handleProductClick(getDefaultCommercialTermsProduct),
      }, {
        commissionItem: {
          disabled: isNotNullOrUndefined(commissionProduct),
        },
        commercialTermsItem: {
          disabled: isNotNullOrUndefined(commercialTermsProduct),
        },
      })}
      className="w-auto whitespace-nowrap"
    >
      <button
        className="group mb-[180px] flex w-fit cursor-pointer items-center space-x-1.5"
        onClick={onClick}
        {...getSelectorAttribute(addItemButtonSelectors.button)}
      >
        <PlusIcon className='text-icon  group-hover:text-style' />
        <label className="text-icon group-hover:text-style cursor-pointer" >
          Add item
        </label>
      </button>
    </Menu>
  );
};

function getCorrectFieldArrayPath(product: FixedProductInput | PlanInputFormUsageProduct): FieldArrayPath<PlanInputForm> {
  if (isFixedProduct(product)) {
    return 'fixedProducts';
  }

  if (isUsageProduct(product)) {
    return 'usageProducts';
  }

  return raise('Invalid product type');
}